<template>
  <div class="category-list-view">
    <section class="toolbar-card">
      <div>
        <h2>产品分类 Product Categories</h2>
        <p>维护产品分类和颜色分组，供产品、颜色和前台页面复用。</p>
      </div>
      <div class="toolbar-card__actions">
        <el-input
          v-model.trim="keyword"
          clearable
          placeholder="搜索名称/标识 Search name/slug"
          size="small"
          @keyup.enter.native="handleFilterChange"
          @clear="handleFilterChange"
        />
        <el-select v-model="typeFilter" clearable placeholder="类型 Type" size="small" @change="handleFilterChange">
          <el-option label="产品 Product" value="product" />
          <el-option label="颜色 Colour" value="colour" />
        </el-select>
        <el-select v-model="statusFilter" clearable placeholder="状态 Status" size="small" @change="handleFilterChange">
          <el-option label="草稿 Draft" value="draft" />
          <el-option label="启用 Active" value="active" />
        </el-select>
        <el-button size="small" @click="handleFilterChange">查询 Search</el-button>
        <el-button type="primary" size="small" @click="openCreateDialog">新增分类 Add Category</el-button>
      </div>
    </section>

    <section class="table-card">
      <el-table :data="categories" v-loading="loading" stripe>
        <el-table-column prop="name" label="名称 Name" min-width="180" />
        <el-table-column prop="slug" label="标识 Slug" min-width="180" />
        <el-table-column prop="type" label="类型 Type" min-width="140" />
        <el-table-column label="主图 Image" min-width="130">
          <template slot-scope="{ row }">
            <el-image v-if="row.image" class="table-thumb" :src="assetUrl(row.image)" :preview-src-list="[assetUrl(row.image)]" :alt="row.name" fit="cover" />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述 Description" min-width="240" show-overflow-tooltip />
        <el-table-column label="排序 Sort Order" min-width="150">
          <template slot-scope="{ row }">
            <el-input-number
              class="sort-input"
              :value="row.sortOrder"
              :min="0"
              :step="1"
              size="mini"
              @change="value => updateCategorySortOrder(row, value)"
            />
          </template>
        </el-table-column>
        <el-table-column label="状态 Status" min-width="150">
          <template slot-scope="{ row }">
            <el-switch
              :value="row.status === 'active'"
              active-text="启用 Active"
              inactive-text="草稿 Draft"
              @change="value => toggleCategoryStatus(row, value)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作 Actions" min-width="200" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" @click="previewCategory(row)">预览 Preview</el-button>
            <el-button type="text" @click="openEditDialog(row)">编辑 Edit</el-button>
            <el-button type="text" class="danger-btn" @click="handleDelete(row)">删除 Delete</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="pagination.total"
          @current-change="handlePageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </section>

    <el-dialog :title="dialogMode === 'create' ? '新增分类 Add Category' : '编辑分类 Edit Category'" :visible.sync="dialogVisible" width="620px" @closed="resetForm">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <div class="form-grid">
          <el-form-item label="名称 Name" prop="name">
            <el-input v-model.trim="form.name" />
          </el-form-item>
          <el-form-item label="标识 Slug" prop="slug">
            <el-input v-model.trim="form.slug" @input="handleSlugInput" />
          </el-form-item>
        </div>

        <div class="form-grid">
          <el-form-item label="类型 Type" prop="type">
            <el-select v-model="form.type" placeholder="请选择 Please select">
              <el-option label="产品 Product" value="product" />
              <el-option label="颜色 Colour" value="colour" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态 Status" prop="status">
            <el-select v-model="form.status" placeholder="请选择 Please select">
              <el-option label="草稿 Draft" value="draft" />
              <el-option label="启用 Active" value="active" />
            </el-select>
          </el-form-item>
        </div>

        <el-form-item label="排序 Sort Order" prop="sortOrder">
          <el-input-number v-model="form.sortOrder" :min="0" :step="1" />
        </el-form-item>

        <el-form-item label="主图 Image">
          <div class="image-field">
            <el-input v-model.trim="form.image" placeholder="图片地址 Image URL" />
            <div class="image-field__actions">
              <el-upload action="" :http-request="uploadImage" :show-file-list="false" accept="image/*">
                <el-button size="small" :loading="uploading">上传 Upload</el-button>
              </el-upload>
              <el-button size="small" @click="openMediaPicker">选择 Choose</el-button>
            </div>
          </div>
          <el-image v-if="form.image" class="image-preview" :src="assetUrl(form.image)" :preview-src-list="[assetUrl(form.image)]" fit="cover" />
        </el-form-item>

        <el-form-item label="描述 Description">
          <el-input v-model.trim="form.description" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="dialogVisible = false">取消 Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">保存 Save</el-button>
      </span>
    </el-dialog>

    <media-picker-dialog
      :visible.sync="mediaPickerVisible"
      upload-module="category"
      @select="chooseMedia"
    />
  </div>
</template>

<script>
import { createCategory, deleteCategory, fetchCategories, updateCategory } from '../../api/products'
import { resolveAssetUrl, uploadMediaAsset } from '../../api/media'
import MediaPickerDialog from '../../components/MediaPickerDialog.vue'
import { categoryPreviewUrl, openPreviewUrl } from '../../utils/preview'
import { toSlug } from '../../utils/slug'

const createEmptyForm = () => ({
  id: null,
  name: '',
  slug: '',
  type: 'product',
  image: '',
  description: '',
  sortOrder: 0,
  status: 'active'
})

export default {
  name: 'CategoryListView',
  components: {
    MediaPickerDialog
  },
  data () {
    return {
      loading: false,
      submitting: false,
      uploading: false,
      dialogVisible: false,
      mediaPickerVisible: false,
      dialogMode: 'create',
      slugTouched: false,
      keyword: '',
      typeFilter: '',
      statusFilter: '',
      categories: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      form: createEmptyForm(),
      rules: {
        name: [{ required: true, message: '请输入名称 Please input name.', trigger: 'blur' }],
        slug: [{ required: true, message: '请输入标识 Please input slug.', trigger: 'blur' }],
        type: [{ required: true, message: '请选择类型 Please select type.', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态 Please select status.', trigger: 'change' }]
      }
    }
  },
  async created () {
    await this.loadCategories()
  },
  watch: {
    'form.name' (value) {
      if (!this.slugTouched) {
        this.form.slug = toSlug(value)
      }
    }
  },
  methods: {
    categoryQueryParams () {
      return {
        page: this.pagination.page,
        pageSize: this.pagination.pageSize,
        keyword: this.keyword || undefined,
        type: this.typeFilter || undefined,
        status: this.statusFilter || undefined
      }
    },
    async loadCategories () {
      this.loading = true
      try {
        const { data } = await fetchCategories(this.categoryQueryParams())
        this.categories = Array.isArray(data) ? data : data.items
        this.pagination.total = Array.isArray(data) ? data.length : data.total
      } catch (error) {
        this.$message.error('分类数据加载失败 Failed to load category data.')
      } finally {
        this.loading = false
      }
    },
    handleFilterChange () {
      this.pagination.page = 1
      this.loadCategories()
    },
    handlePageChange (page) {
      this.pagination.page = page
      this.loadCategories()
    },
    handlePageSizeChange (pageSize) {
      this.pagination.page = 1
      this.pagination.pageSize = pageSize
      this.loadCategories()
    },
    openCreateDialog () {
      this.dialogMode = 'create'
      this.slugTouched = false
      this.dialogVisible = true
    },
    openEditDialog (row) {
      this.dialogMode = 'edit'
      this.slugTouched = true
      this.form = {
        id: row.id,
        name: row.name,
        slug: row.slug,
        type: row.type,
        image: row.image || '',
        description: row.description || '',
        sortOrder: row.sortOrder,
        status: row.status
      }
      this.dialogVisible = true
    },
    resetForm () {
      this.form = createEmptyForm()
      this.slugTouched = false
      if (this.$refs.form) this.$refs.form.resetFields()
    },
    handleSlugInput () {
      this.slugTouched = true
      this.form.slug = toSlug(this.form.slug)
    },
    assetUrl (fileUrl) {
      return resolveAssetUrl(fileUrl)
    },
    openMediaPicker () {
      this.mediaPickerVisible = true
    },
    chooseMedia (item) {
      this.form.image = item.fileUrl
      this.mediaPickerVisible = false
    },
    previewCategory (row) {
      openPreviewUrl(categoryPreviewUrl(row))
    },
    async uploadImage ({ file }) {
      if (this.uploading) return
      this.uploading = true
      try {
        const { data } = await uploadMediaAsset(file, 'category')
        this.form.image = data.fileUrl
        this.$message.success('图片已上传 Image uploaded.')
      } catch (error) {
        this.$message.error((error.response && error.response.data && error.response.data.message) || '上传失败 Upload failed.')
      } finally {
        this.uploading = false
      }
    },
    handleSubmit () {
      this.$refs.form.validate(async valid => {
        if (!valid || this.submitting) return
        this.submitting = true
        try {
          if (this.dialogMode === 'create') {
            await createCategory(this.form)
            this.$message.success('分类已创建 Category created.')
          } else {
            await updateCategory(this.form.id, this.form)
            this.$message.success('分类已更新 Category updated.')
          }
          this.dialogVisible = false
          await this.loadCategories()
        } catch (error) {
          this.$message.error((error.response && error.response.data && error.response.data.message) || '保存失败 Save failed.')
        } finally {
          this.submitting = false
        }
      })
    },
    buildCategoryPayload (row, overrides = {}) {
      return {
        name: row.name,
        slug: row.slug,
        type: row.type,
        image: row.image || '',
        description: row.description || '',
        sortOrder: row.sortOrder || 0,
        status: row.status || 'draft',
        ...overrides
      }
    },
    async toggleCategoryStatus (row, enabled) {
      const previousStatus = row.status
      const nextStatus = enabled ? 'active' : 'draft'
      row.status = nextStatus

      try {
        await updateCategory(row.id, this.buildCategoryPayload(row, { status: nextStatus }))
        this.$message.success('状态已更新 Status updated.')
      } catch (error) {
        row.status = previousStatus
        this.$message.error((error.response && error.response.data && error.response.data.message) || '状态更新失败 Failed to update status.')
      }
    },
    async updateCategorySortOrder (row, value) {
      const previousSortOrder = row.sortOrder || 0
      const nextSortOrder = Number(value || 0)
      row.sortOrder = nextSortOrder

      try {
        await updateCategory(row.id, this.buildCategoryPayload(row, { sortOrder: nextSortOrder }))
        this.$message.success('排序已更新 Sort order updated.')
      } catch (error) {
        row.sortOrder = previousSortOrder
        this.$message.error((error.response && error.response.data && error.response.data.message) || '排序更新失败 Failed to update sort order.')
      }
    },
    handleDelete (row) {
      this.$confirm(`确认删除分类“${row.name}”吗？\nConfirm deleting this category?`, '删除确认 Delete Confirmation', {
        type: 'warning'
      }).then(async () => {
        try {
          await deleteCategory(row.id)
          this.$message.success('分类已删除 Category deleted.')
          await this.loadCategories()
        } catch (error) {
          this.$message.error((error.response && error.response.data && error.response.data.message) || '删除失败 Delete failed.')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.toolbar-card,
.table-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  box-shadow: 0 16px 32px rgba(23, 20, 17, 0.04);
}

.toolbar-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 24px;
}

.toolbar-card h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 500;
}

.toolbar-card p {
  margin: 8px 0 0;
  color: var(--muted);
}

.toolbar-card__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.toolbar-card__actions .el-input,
.toolbar-card__actions .el-select {
  width: 180px;
}

.table-card {
  margin-top: 18px;
  padding: 12px;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  padding: 14px 4px 2px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.sort-input {
  width: 112px;
}

.danger-btn {
  color: #d14343;
}

.table-thumb {
  display: block;
  width: 72px;
  height: 54px;
  object-fit: cover;
  border-radius: 6px;
  background: #f2eee8;
}

.image-preview {
  display: block;
  width: 220px;
  max-width: 100%;
  aspect-ratio: 4 / 3;
  margin-top: 10px;
  object-fit: cover;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #f2eee8;
}

.image-field {
  display: grid;
  gap: 10px;
}

.image-field__actions,
.picker-toolbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.picker-toolbar {
  margin-bottom: 16px;
}

.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  min-height: 260px;
}

.picker-card {
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
  text-align: left;
  cursor: pointer;
}

.picker-card .el-image {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  background: #f2eee8;
}

.picker-card span {
  display: block;
  overflow: hidden;
  padding: 9px 10px;
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 960px) {
  .toolbar-card,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .toolbar-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar-card__actions {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
